import axios from 'axios'

// Configuration
const API_TIMEOUT = 30000 // 30 secondes
const MAX_RETRIES = 3
const RETRY_DELAY = 1000 // 1 seconde

// Helper pour normaliser l'URL de l'API
const getApiBaseUrl = () => {
  return process.env.REACT_APP_APOLLO_URL || 'https://extranet.biobreizh.org'
}

// Helper pour créer une instance axios avec timeout
const createAxiosInstance = (timeout = API_TIMEOUT) => {
  return axios.create({
    timeout,
    headers: {
      'Content-Type': 'application/json',
    }
  })
}

// Fonction pour réessayer une requête en cas d'échec
const retryRequest = async (requestFn, maxRetries = MAX_RETRIES, delay = RETRY_DELAY) => {
  let lastError
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await requestFn()
    } catch (error) {
      lastError = error
      
      // Vérifier si l'erreur est récupérable (problème réseau, timeout, 5xx)
      const isRecoverable = !error.response || 
                           error.code === 'ECONNABORTED' || 
                           (error.response && error.response.status >= 500)
      
      if (!isRecoverable) {
        throw error
      }
      
      console.warn(`Tentative ${attempt + 1}/${maxRetries} échouée, nouvelle tentative dans ${delay}ms...`)
      await new Promise(resolve => setTimeout(resolve, delay))
      
      // Augmenter le délai pour les tentatives suivantes (backoff exponentiel)
      delay *= 2
    }
  }
  
  throw lastError
}

// Fonction améliorée pour sauvegarder un brouillon
export const saveDraftToServer = async (formData, step, app, serverDraftId, aidetype, mesure, title) => {
  if (!app || !app.profil || !app.profil.id) {
    console.error('Erreur: Informations utilisateur manquantes')
    throw new Error('Informations utilisateur manquantes')
  }
  
  if (!formData) {
    console.error('Erreur: Données du formulaire manquantes')
    throw new Error('Données du formulaire manquantes')
  }
  
  try {
    console.log('Sauvegarde du brouillon, étape:', step, 'ID existant:', serverDraftId || 'nouveau')

    const apiFormData = new FormData()
    apiFormData.append('user_id', app.profil.id)
    apiFormData.append('current_step', step)
    apiFormData.append('form_type_json', JSON.stringify({
      aidetype: aidetype || '',
      mesure: mesure || '',
      title: title || ''
    }))

    // Créer une copie profonde des données pour éviter de modifier l'original
    let formDataWithoutFiles = JSON.parse(JSON.stringify(formData))

    // Traiter les fichiers et autres types de données spéciaux
    Object.keys(formData).forEach(groupKey => {
      if (typeof formData[groupKey] === 'object' && formData[groupKey] !== null) {
        Object.keys(formData[groupKey]).forEach(fieldKey => {
          const field = formData[groupKey][fieldKey]

          if (field instanceof File) {
            const fileId = `${groupKey}_${fieldKey}_${Date.now()}`
            const fileFieldName = `file_${fileId}`

            apiFormData.append(fileFieldName, field)

            formDataWithoutFiles[groupKey][fieldKey] = {
              name: field.name,
              type: field.type,
              size: field.size,
              fileId: fileId,
              isFile: true,
              lastModified: field.lastModified
            }
            console.log(`Fichier ajouté: ${groupKey}.${fieldKey}`, field.name)
          }
          else if (field && typeof field === 'object' && (field.isFile || field.isFileReference)) {
            console.log(`Fichier existant conservé: ${groupKey}.${fieldKey}`, field.name)

            if (!field.fileId) {
              formDataWithoutFiles[groupKey][fieldKey].fileId = `${groupKey}_${fieldKey}_${Date.now()}`
            }

            formDataWithoutFiles[groupKey][fieldKey].isFile = true
          }
          else if (field instanceof Date) {
            formDataWithoutFiles[groupKey][fieldKey] = field.toISOString()
          }
        })
      }
    })

    console.log('Données préparées pour l\'envoi')
    apiFormData.append('form_data_json', JSON.stringify(formDataWithoutFiles))

    if (serverDraftId) {
      apiFormData.append('draft_id', serverDraftId)
    }

    // Utiliser la fonction de retry pour la requête
    const response = await retryRequest(async () => {
      return axios.post(
        `${getApiBaseUrl()}/wp-json/bb/v1/po/draft`,
        apiFormData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          timeout: API_TIMEOUT
        }
      )
    })

    if (response.status === 200 && response.data && response.data.draft_id) {
      console.log('Brouillon sauvegardé avec succès, ID:', response.data.draft_id)
      return response.data.draft_id
    }

    console.error('Réponse invalide lors de la sauvegarde du brouillon:', response)
    throw new Error('Réponse invalide du serveur')
  } catch (error) {
    console.error('Erreur lors de la sauvegarde du brouillon:', error)
    throw error
  }
}

// Fonction améliorée pour charger un brouillon
export const loadDraftFromServer = async (draftId, apiUrl) => {
  if (!draftId) {
    console.error('Erreur: ID du brouillon manquant')
    throw new Error('ID du brouillon manquant')
  }
  
  try {
    // Utiliser la fonction de retry pour la requête
    const response = await retryRequest(async () => {
      return createAxiosInstance().get(`${getApiBaseUrl()}/wp-json/bb/v1/po/draft/${draftId}`)
    })
    
    if (response.status === 200 && response.data) {
      // Valider la structure des données reçues
      if (!response.data.form_data || !response.data.form_type) {
        console.error('Structure de brouillon invalide:', response.data)
        throw new Error('Structure de brouillon invalide')
      }
      
      return response.data
    }
    
    throw new Error(`Erreur lors du chargement du brouillon: ${response.status}`)
  } catch (error) {
    console.error('Erreur lors du chargement du brouillon depuis le serveur:', error)
    throw error
  }
}

/**
 * Traite les données du formulaire pour les adapter au format attendu par l'interface utilisateur
 * @param {Object} formData - Les données brutes du formulaire à traiter
 * @returns {Object} Les données traitées
 */
export const processFormData = (formData) => {
  // Vérifier si les données sont valides
  if (!formData) return {}
  
  try {
    // Créer une copie profonde des données pour éviter de modifier l'original
    let processedData = JSON.parse(JSON.stringify(formData))
    
    // Parcourir tous les groupes de données
    Object.keys(processedData).forEach(groupKey => {
      // Vérifier si le groupe est un objet valide
      if (typeof processedData[groupKey] === 'object' && processedData[groupKey] !== null) {
        // Parcourir tous les champs du groupe
        Object.keys(processedData[groupKey]).forEach(fieldKey => {
          const field = processedData[groupKey][fieldKey]
          
          try {
            // Convertir les chaînes de date en objets Date pour les datepickers
            if (typeof field === 'string' && field.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
              processedData[groupKey][fieldKey] = new Date(field)
            }
            
            // Traiter les champs de fichiers
            if (field && typeof field === 'object' && field.isFile) {
              // Générer un ID de fichier s'il n'existe pas
              if (!field.fileId) {
                field.fileId = `${groupKey}_${fieldKey}_${Date.now()}`
              }
              
              // Ajouter une URL de prévisualisation si elle n'existe pas mais que l'URL est disponible
              if (!field.preview && field.url) {
                field.preview = field.url
              }
              
              // S'assurer que les fichiers sont correctement marqués comme modifiables
              field.isFileReference = true
            }
            
            // S'assurer que les valeurs null sont converties en chaînes vides pour les champs de texte
            if (field === null) {
              processedData[groupKey][fieldKey] = ''
            }
            
            // Convertir les tableaux vides en tableaux réels pour éviter les problèmes avec certains composants
            if (field === undefined && Array.isArray(processedData[groupKey][fieldKey])) {
              processedData[groupKey][fieldKey] = []
            }
          } catch (fieldError) {
            console.error(`Erreur lors du traitement du champ ${groupKey}.${fieldKey}:`, fieldError)
            // Conserver la valeur originale en cas d'erreur
          }
        })
      }
    })
    
    console.log('Données traitées')
    return processedData
  } catch (error) {
    console.error('Erreur lors du traitement des données du formulaire:', error)
    // Retourner un objet vide en cas d'erreur pour éviter les plantages
    return {}
  }
}
